// clientRoutes/_app.$usernameRaw.tsx
//
// Layout client module for user profile routes.
// Renders the shared profile chrome (provider, info header, container)
// so that tab switches only re-render the <Outlet/> content.
import { skipToken } from '@apollo/client/react'
import { useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Outlet, useParams } from 'react-router'
import { withoutTrailingSlash } from 'ufo'
import Empty from 'src/components/Empty'
import LoadingPage from 'src/components/LoadingPage'
import { consumeProfileScroll } from 'src/components/UserProfile/profileScrollRestore'
import { useProfileTab } from 'src/components/UserProfile/useProfileTab'
import UserProfileInfo from 'src/components/UserProfile/UserProfileInfo'
import UserProfileProvider from 'src/components/UserProfile/UserProfileProvider'
import { useGetUserInfoByUsername } from 'src/services/apollo/user'
import { useLocation } from 'src/utils/router/route'

export default function UserProfileLayout() {
  const { t } = useTranslation(['common'])
  const location = useLocation()
  const { usernameRaw } = useParams()
  const username = usernameRaw?.slice(1)
  const tab = useProfileTab()

  const {
    data: user,
    loading: loadingUser,
    error,
  } = useGetUserInfoByUsername(username ?? skipToken)

  useLayoutEffect(() => {
    if (!user) return
    const scrollY = consumeProfileScroll()
    if (scrollY != null) {
      window.scrollTo(0, scrollY)
    }
  }, [user, tab, location.key])

  if (withoutTrailingSlash(location.pathname) === `/@${username}`) {
    return <Navigate to="artworks" relative="path" replace />
  }

  if (user) {
    return (
      <UserProfileProvider user={user} tab={tab}>
        <div className="mmd:mt-12 mmd:mb-5 mmd:p-12 mmd:pb-4 mmd:bg-white/90 dark:mmd:bg-black/50 mmd:ring-1 dark:ring-0 ring-black/5 rounded-lg mmd:shadow-lg dark:mmd:![box-shadow:0_-1px_1px_#fff2]">
          <div className="flex flex-col gap-8 relative">
            <UserProfileInfo />
            <Outlet />
          </div>
        </div>
      </UserProfileProvider>
    )
  }

  if (!loadingUser) {
    return (
      <Empty
        description={error?.toString() || t('common:hint.user-not-found')}
      />
    )
  }

  return <LoadingPage />
}
